import React, { MouseEventHandler, useState } from 'react'
import {
  ToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  styled,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

export type RadioOption = {
  value: string | number
  label: string
  hidden?: boolean
}

export type RadioButtonProps = {
  defaultState?: string | null
  options: RadioOption[]
  id: string
  valueChanged?: (_: string) => void
  onClick?: MouseEventHandler<HTMLLabelElement>
  error?: string | JSX.Element
  direction?: 'horizontal' | 'vertical'
  extendable?: boolean
  title?: string,
  oneLine?: boolean
}

type StyledMuiToggleButtonGroupProps = {
  oneLine?: boolean
  extendable?: boolean
}

const StyledMuiToggleButtonGroup = styled(MuiToggleButtonGroup,
  {
    shouldForwardProp: (prop) => prop !== 'oneLine' && prop !== 'extendable'
  })<StyledMuiToggleButtonGroupProps>(({ theme, oneLine, extendable }) => ({
    display: "grid",
    gridTemplateColumns: !oneLine ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
    gridGap: !oneLine ? theme.spacing(4) : theme.spacing(2),
    gridRowGap: theme.spacing(2),
    marginBottom: (oneLine || extendable) ? theme.spacing(0) : theme.spacing(9),
    marginTop: (oneLine || extendable) ? theme.spacing(4) : theme.spacing(6)
  }))

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: 'none',
  borderTop: `1px solid ${theme.customPalette.neutral[80]}`,
  borderBottom: `1px solid ${theme.customPalette.neutral[80]} !important`,
  backgroundColor: 'transparent',
  marginTop: theme.spacing(4),
  '&::before': {
    display: 'none'
  },
  '&.Mui-expanded': {
    margin: 0,
    borderBottom: `1px solid ${theme.customPalette.neutral[80]} !important`,
    marginTop: theme.spacing(4)
  },
  '& .MuiAccordionSummary-root': {
    padding: `${theme.spacing(5)} ${theme.spacing(0)}`,
    minHeight: 'auto',
    'span, svg': {
      color: `${theme.customPalette.neutral[50]} !important`
    },
    '&:hover': {
      'span, svg': {
        color: `${theme.customPalette.primary[40]} !important`
      }
    },
    '&.Mui-expanded': {
      minHeight: 'auto',
      'svg': {
        color: `${theme.customPalette.primary[40]} !important`
      }
    }
  },
  '& .MuiAccordionDetails-root': {
    padding: 0
  },
  '.MuiToggleButtonGroup-root': {
    marginTop: 0,
    marginBottom: theme.spacing(5)
  }
}))

const ToggleButtonGroup = ({
  options,
  defaultState,
  id,
  direction = 'horizontal',
  valueChanged,
  extendable = false,
  title,
  oneLine = false
}: RadioButtonProps): JSX.Element => {
  const [option, setOption] = useState(() => defaultState)

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newOption: string | null
  ) => {
    if (newOption !== null) {
      setOption(newOption)
    }
    valueChanged && valueChanged(newOption as string)
  }

  const generateOptions = ({ value, label }: RadioOption) => {
    const optionId = `${id}+${value.toString()}`
    return (
      <ToggleButton
        key={optionId}
        value={value}
        onClick={handleChange}
        id={`${id}+${value}`}
      >
        {label}
      </ToggleButton>
    )
  }

  if (!extendable) {
    return (
      <StyledMuiToggleButtonGroup
        id={id}
        title={title}
        orientation={direction}
        color="primary"
        value={option}
        exclusive
        aria-label={title}
        oneLine={oneLine}
      >
        {options.map(option => generateOptions(option))}
      </StyledMuiToggleButtonGroup>
    )
  }

  const normalOptions = options.filter((option) => !option.hidden)
  const hiddenOptions = options.filter((option) => option.hidden)
  const open = hiddenOptions.some(option => option.value === defaultState)
  return (
    <>
      <StyledMuiToggleButtonGroup
        id={id}
        title={title}
        orientation={direction}
        color="primary"
        value={option}
        exclusive
        aria-label={title}
        extendable={extendable}
      >
        {normalOptions.map(option => generateOptions(option))}
      </StyledMuiToggleButtonGroup>
      <StyledAccordion defaultExpanded={open}>
        <AccordionSummary
          expandIcon={<KeyboardArrowDownIcon fontSize="medium" />}
          aria-controls="accordion-show-more"
          id="accordion-show-more"
        >
          <Typography variant="rowHeading" component="span" aria-controls="panel1-content" id="panel1-header">Show more options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <StyledMuiToggleButtonGroup
            id={`${id}-extended`}
            title={title}
            orientation={direction}
            color="primary"
            value={option}
            exclusive
            aria-label={title}
          >
            {hiddenOptions.map(option => generateOptions(option))}
          </StyledMuiToggleButtonGroup>
        </AccordionDetails>
      </StyledAccordion >

    </>
  )
}

export default ToggleButtonGroup
